/**
 * DoctorsSection.jsx
 * components/user/sections/DoctorsSection.jsx
 * Displays active doctors — no add/edit/delete for user side (read-only view)
 * Admin CRUD is separate; user side just books via BookAppointmentModal
 */
import { useState, useEffect } from "react";
import { apiFetch } from "../../helpers";

const SPECIALIZATIONS = ["All", "General Dentist", "Orthodontist", "Endodontist", "Periodontist", "Oral Surgeon", "Pediatric Dentist"];

export default function DoctorsSection({ onBookAppointment }) {
  const [doctors, setDoctors]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter,  setFilter]          = useState("All");
  const [search,  setSearch]          = useState("");
  const [selected, setSelected]       = useState(null); // doctor detail modal

  useEffect(() => {
    (async () => {
      try {
        const json = await apiFetch(`${process.env.REACT_APP_GET_ACTIVE_DOCTOR}`);
        if (json.status) setDoctors(json.data || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const filtered = doctors.filter(d => {
    const matchSpec = filter === "All" || d.specialization === filter;
    const matchSearch = d.name?.toLowerCase().includes(search.toLowerCase()) ||
                        d.specialization?.toLowerCase().includes(search.toLowerCase());
    return matchSpec && matchSearch;
  });

  const S = styles;

  return (
    <section id="doctors" style={S.section}>
      <div style={{
        padding: 33,
      }}></div>
      {/* Background decoration */}
      <div style={S.bgDeco1} />
      <div style={S.bgDeco2} />

      <div style={S.container}>
        {/* Heading */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span style={S.pill}>👨‍⚕️ Our Specialists</span>
          <h2 style={S.heading}>Meet Our <span style={S.green}>Expert Doctors</span></h2>
          <p style={S.subHeading}>Trusted professionals dedicated to your perfect smile</p>
        </div>

        {/* Search + Filter */}
        <div style={{ display: "flex", gap: 12, marginBottom: 32, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>🔍</span>
            <input
              placeholder="Search doctors..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={S.searchInput}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SPECIALIZATIONS.map(sp => (
              <button key={sp} onClick={() => setFilter(sp)}
                style={{ ...S.filterBtn, ...(filter === sp ? S.filterBtnActive : {}) }}>
                {sp}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <div className="spinner-border" style={{ color: "#1D9E75", width: 40, height: 40 }} />
            <p style={{ color: "rgba(255,255,255,.5)", marginTop: 16 }}>Loading doctors...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem", color: "rgba(255,255,255,.4)" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <p>No doctors found matching your search.</p>
          </div>
        ) : (
          <div style={S.grid}>
            {filtered.map(doc => (
              <DoctorCard key={doc.doctor_id} doc={doc}
                onView={() => setSelected(doc)}
                onBook={() => onBookAppointment && onBookAppointment(doc)} />
            ))}
          </div>
        )}
      </div>

      {/* Doctor Detail Modal */}
      {selected && (
        <DoctorModal doc={selected} onClose={() => setSelected(null)}
          onBook={() => { setSelected(null); onBookAppointment && onBookAppointment(selected); }} />
      )}
    </section>
  );
}

function DoctorCard({ doc, onView, onBook }) {
  const S = styles;
  return (
    <div style={S.card}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-6px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "none"}>
      <div style={S.cardImgWrap}>
        {doc.profile_image
          ? <img src={`/uploads/${doc.profile_image}`} alt={doc.name} style={S.cardImg} />
          : <div style={S.cardAvatar}>{doc.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}</div>}
        <div style={S.cardBadge}>{doc.specialization}</div>
      </div>
      <div style={S.cardBody}>
        <h3 style={S.cardName}>{doc.name}</h3>
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          {doc.experience && <span style={S.metaTag}>🏅 {doc.experience} yrs exp</span>}
          {doc.qualification && <span style={S.metaTag}>🎓 {doc.qualification}</span>}
        </div>
        <p style={S.cardBio}>{doc.bio || "Dedicated dental professional committed to providing exceptional patient care."}</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onView} style={S.btnOutline}>View Profile</button>
          {/* <button onClick={onBook} style={S.btnPrimary}>📅 Book</button> */}
        </div>
      </div>
    </div>
  );
}

function DoctorModal({ doc, onClose, onBook }) {
  const S = styles;
  return (
    <div onClick={onClose} style={S.overlay}>
      <div onClick={e => e.stopPropagation()} style={{ ...S.modal, maxWidth: 520 }}>
        <button onClick={onClose} style={S.closeBtn}>✕</button>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 100, height: 100, borderRadius: "50%", margin: "0 auto 12px", overflow: "hidden", border: "3px solid #1D9E75" }}>
            {doc.profile_image
              ? <img src={`/uploads/${doc.profile_image}`} alt={doc.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ ...S.cardAvatar, width: "100%", height: "100%", fontSize: 32 }}>{doc.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}</div>}
          </div>
          <h2 style={{ color: "#fff", fontWeight: 800, marginBottom: 4 }}>{doc.name}</h2>
          <span style={S.cardBadge}>{doc.specialization}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          {[
            ["🎓 Qualification", doc.qualification || "—"],
            ["🏅 Experience", doc.experience ? `${doc.experience} years` : "—"],
            ["📧 Email", doc.email || "—"],
            ["📞 Phone", doc.phone || "—"],
          ].map(([label, val]) => (
            <div key={label} style={{ background: "rgba(255,255,255,.05)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{val}</div>
            </div>
          ))}
        </div>
        {doc.bio && <p style={{ color: "rgba(255,255,255,.65)", fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>{doc.bio}</p>}
        {/* <button onClick={onBook} style={{ ...S.btnPrimary, width: "100%", justifyContent: "center", padding: "12px" }}>
          📅 Book Appointment with {doc.name?.split(" ")[0]}
        </button> */}
      </div>
    </div>
  );
}

/* ── shared styles ── */
const styles = {
  section: { position: "relative", padding: "100px 0", background: "linear-gradient(180deg,#0A1628 0%,#0D1F3C 100%)", overflow: "hidden" },
  bgDeco1: { position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(29,158,117,.08) 0%,transparent 70%)", pointerEvents: "none" },
  bgDeco2: { position: "absolute", bottom: -80, left: -80, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(232,50,60,.06) 0%,transparent 70%)", pointerEvents: "none" },
  container: { maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem", position: "relative" },
  pill: { display: "inline-block", background: "rgba(29,158,117,.15)", border: "1px solid rgba(29,158,117,.3)", color: "#1D9E75", padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 16 },
  heading: { fontSize: "clamp(28px,4vw,42px)", fontWeight: 900, color: "#fff", marginBottom: 12, lineHeight: 1.2 },
  green: { color: "#1D9E75" },
  subHeading: { color: "rgba(255,255,255,.5)", fontSize: 15, maxWidth: 500, margin: "0 auto" },
  searchInput: { width: "100%", padding: "10px 10px 10px 38px", borderRadius: 8, border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box" },
  filterBtn: { padding: "6px 14px", borderRadius: 20, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: "rgba(255,255,255,.6)", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all .2s" },
  filterBtnActive: { background: "rgba(29,158,117,.2)", border: "1px solid #1D9E75", color: "#1D9E75" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 24 },
  card: { background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 16, overflow: "hidden", transition: "transform .25s,box-shadow .25s", cursor: "pointer" },
  cardImgWrap: { position: "relative", height: 200, background: "linear-gradient(135deg,#0D2137,#1B2A6B)" },
  cardImg: { width: "100%", height: "100%", objectFit: "cover" },
  cardAvatar: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, fontWeight: 800, color: "#1D9E75", background: "rgba(29,158,117,.1)" },
  cardBadge: { position: "absolute", bottom: 10, left: 10, background: "rgba(29,158,117,.85)", backdropFilter: "blur(6px)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20 },
  cardBody: { padding: "18px 20px" },
  cardName: { fontSize: 17, fontWeight: 800, color: "#fff", marginBottom: 8 },
  metaTag: { fontSize: 11, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.65)", padding: "3px 10px", borderRadius: 20 },
  cardBio: { fontSize: 12, color: "rgba(255,255,255,.45)", lineHeight: 1.6, marginBottom: 16, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  btnOutline: { flex: 1, padding: "8px 12px", borderRadius: 7, border: "1px solid rgba(255,255,255,.2)", background: "transparent", color: "rgba(255,255,255,.8)", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnPrimary: { flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 7, border: "none", background: "#1D9E75", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" },
  modal: { background: "#0D1F3C", border: "1px solid rgba(255,255,255,.1)", borderRadius: 16, padding: "32px 28px", width: "100%", maxHeight: "90vh", overflowY: "auto", position: "relative", fontFamily: "'Nunito','Segoe UI',sans-serif" },
  closeBtn: { position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,.1)", border: "none", borderRadius: 6, color: "#fff", width: 30, height: 30, cursor: "pointer", fontSize: 14 },
};